// Settings → General: recipes on the kitchen screens (kitchen flow, migration
// 148). Admin only, and only with kitchen_flow on — renders nothing otherwise,
// so live tenants see no new setting. Off by default: a tenant gets recipe
// cards by asking for them.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useAllSettings, useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { useToast } from '@/hooks/use-toast'
import { RC } from '@/lib/recipeCard/recipeCardStrings'
import { recipeCardsSwitchedOn } from '@/lib/recipeCard/recipeScale'
import { getStoredUserRole } from '@/lib/staff-roles'
import { plainError } from '@/components/store/inventory/prep/prepRecipeDraft'

export function RecipeCardsSettingCard({ showToast }: { showToast?: (t: 'success' | 'error', m: string) => void }) {
  const enabled = useKitchenFlowEnabled()
  const all = useAllSettings()
  const on = recipeCardsSwitchedOn(all)
  const qc = useQueryClient()
  const { toast } = useToast()
  // Settings → General passes no toast of its own; a refused save must still be seen.
  const say = (t: 'success' | 'error', m: string) =>
    showToast ? showToast(t, m) : t === 'error' ? toast({ variant: 'destructive', title: m }) : undefined
  const save = useMutation({
    mutationFn: (next: boolean) => apiClient.updateSetting('kitchen_flow.recipe_cards', { enabled: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'all'] })
      say('success', RC.settingSaved)
    },
    onError: (e: Error) => say('error', plainError(e)),
  })
  if (!enabled || getStoredUserRole() !== 'admin') return null

  // While a change is in flight the switch shows where it is going.
  const shown = save.isPending ? Boolean(save.variables) : on

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">{RC.settingTitle}</CardTitle>
        <CardDescription>{RC.settingHelp}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-3">
          <Switch id="recipe-cards-enabled" checked={shown} disabled={save.isPending} onCheckedChange={(v) => save.mutate(v)} />
          <Label htmlFor="recipe-cards-enabled">{RC.settingSwitch}</Label>
        </div>
        {shown && <p className="text-sm text-muted-foreground">{RC.settingOnNote}</p>}
      </CardContent>
    </Card>
  )
}
